/**
 * Lectura de números tipeados por una persona. **Un solo parser para todo el
 * panel.**
 *
 * Estaba escrito tres veces — una vez por cada módulo que tiene un formulario de
 * carga — y las tres versiones aceptaban cosas distintas: la de Revenue leía
 * `$74.233`, las otras dos se quedaban en `NaN` con el mismo texto pegado del
 * sheet. Como los tres formularios escriben por el **mismo** endpoint de ingesta,
 * lo que se acepta tiene que ser lo mismo.
 *
 * Después quedó una cuarta, `numeroDeCampo` en el diálogo de métricas de Admin,
 * con la regla contraria para el punto. No era una divergencia inocua: el valor
 * cargado por Ops y el umbral cargado por Admin terminan en el mismo
 * `evaluateHealth` de `@ops/contracts`, así que el semáforo comparaba `3,2`
 * contra `32` según quién lo hubiera tipeado. Ahora las dos entradas pasan por
 * `numeroDeCampo`, que vive acá, y el semáforo compara lo mismo con lo mismo.
 */

/** `1.234` y `1.234.567`: punto seguido de grupos de tres, sin coma decimal. */
const MILES_ES_AR = /^-?\d{1,3}(\.\d{3})+$/

/**
 * Un número y nada más: dígitos, puntos, comas y el signo. Sin esto `Number`
 * aceptaría `1e5`, `0x10` e `Infinity`, que nadie quiso escribir en un campo de
 * MRR y que pasarían silenciosamente al endpoint de ingesta.
 */
const FORMA_NUMERICA = /^-?[\d.,]+$/

/**
 * Lee un número escrito en es-AR sin adivinar de más.
 *
 * `''` → null (un umbral vacío es "sin umbral", nunca cero) · `'1.234,5'` →
 * 1234.5 · `'75.000'` → 75000 · `'0.5'` → 0.5 · basura → NaN, que el schema
 * rechaza con las mismas palabras que usaría el servidor.
 *
 * El caso peligroso es `'75.000'`: leerlo como 75 pondría el semáforo de MRR mil
 * veces más abajo sin que nadie lo note. Por eso el punto se trata como separador
 * de miles solo cuando la forma es inequívocamente de miles, y el campo es de
 * texto y no `type="number"` — así el navegador no impone otra interpretación.
 * Y al revés: `'3.2'` es tres coma dos, no treinta y dos, porque nadie escribe
 * un separador de miles de un solo dígito.
 */
export function numeroDeCampo(texto: string): number | null {
  const limpio = texto.trim()
  if (limpio === '') return null
  if (limpio.includes(',')) {
    return Number(limpio.replace(/\./g, '').replace(',', '.'))
  }
  if (MILES_ES_AR.test(limpio)) return Number(limpio.replace(/\./g, ''))
  return Number(limpio)
}

/**
 * Resultado de leer una celda o un input.
 *
 * `vacio` es un caso propio y no un cero: una celda borrada deja el mes **sin
 * dato**, que no es lo mismo que un mes que facturó cero. Colapsar los dos es
 * cómo un sheet termina con ceros que nadie cargó.
 */
export type NumeroLeido =
  | { ok: true; valor: number }
  | { ok: true; vacio: true; valor: null }
  | { ok: false }

/**
 * Lee un número escrito en es-AR o pegado del sheet: `$74.233`, `21,7`, `−4,2`,
 * `1 200`. Delega la ambigüedad del punto en `numeroDeCampo` y agrega lo que un
 * formulario necesita y un umbral no: sacar el `$` y el `%` que vienen pegados
 * del sheet, aceptar el menos tipográfico (U+2212) que escribe nuestro propio
 * formateador —así copiar un valor de la pantalla y volver a pegarlo funciona— y
 * sobre todo **distinguir vacío de ilegible**.
 */
export function leerNumero(texto: string): NumeroLeido {
  const limpio = texto
    .replace(/\s/g, '')
    .replace(/\$/g, '')
    .replace(/%/g, '')
    .replace(/−/g, '-')

  if (limpio === '') return { ok: true, vacio: true, valor: null }
  if (!FORMA_NUMERICA.test(limpio)) return { ok: false }

  const n = numeroDeCampo(limpio)
  if (n === null || !Number.isFinite(n)) return { ok: false }
  return { ok: true, valor: n }
}

/**
 * El número, o `null` si el texto está vacío o no se puede leer.
 *
 * Es la forma corta para un formulario de un solo campo, donde "vacío" e
 * "ilegible" llevan al mismo lugar: no hay nada que guardar. Quien necesite
 * distinguirlos usa `leerNumero`.
 */
export function numeroDe(texto: string): number | null {
  const leido = leerNumero(texto)
  return leido.ok ? leido.valor : null
}
